import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';
import { useState, type DragEvent } from 'react';

import { recordsList, recordsReorder } from '@/shared/api/generated/records/records';
import type { RecordBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Skeleton } from '@/shared/ui/Skeleton';
import { toast } from '@/shared/ui/Toast';

import styles from './ContentPage.module.scss';

/** Сколько записей расставляется за раз: больше — уже не ручной порядок. */
const LIMIT = 500;
const PAGE = 100;

/**
 * Ручной порядок коллекции: все записи одним списком, перетаскивание за
 * ручку или стрелками. Порядок сохраняется сразу и сразу виден сайту — у
 * коллекции с ручным порядком сайт получает записи в нём.
 */
export function OrderList({ typeID }: { typeID: string }) {
  const queryClient = useQueryClient();
  const [dragged, setDragged] = useState<number>();
  const [over, setOver] = useState<number>();

  const key = ['records', typeID, { order: 'manual' }];

  const all = useQuery({
    queryKey: key,
    queryFn: async () => {
      const items: RecordBody[] = [];

      for (let page = 1; items.length < LIMIT; page++) {
        const response = await recordsList(typeID, {
          sort: 'position',
          order: 'asc',
          page,
          per_page: PAGE,
        });

        if (response.status !== 200) {
          throw new Error('Неожиданный ответ на запрос записей');
        }

        items.push(...(response.data.items ?? []));

        if (items.length >= response.data.total) {
          break;
        }
      }

      return items;
    },
  });

  const records = all.data ?? [];

  const save = (next: RecordBody[]) => {
    const previous = records;

    queryClient.setQueryData(key, next);

    recordsReorder(typeID, { ids: next.map((record) => record.id) })
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['records', typeID] });
      })
      .catch((error: unknown) => {
        queryClient.setQueryData(key, previous);
        toast.error(error instanceof ApiError ? error.detail : 'Порядок не сохранился');
      });
  };

  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= records.length) {
      return;
    }

    const next = [...records];
    const [item] = next.splice(from, 1);

    if (item !== undefined) {
      next.splice(to, 0, item);
      save(next);
    }
  };

  if (all.isPending) {
    return (
      <div className={styles['order']}>
        <Skeleton height={44} />
        <Skeleton height={44} />
        <Skeleton height={44} />
      </div>
    );
  }

  return (
    <ol className={styles['order']} aria-label="Порядок записей">
      {records.map((record, index) => (
        <li
          key={record.id}
          className={styles['order__item']}
          data-dragging={dragged === index || undefined}
          data-over={
            over === index && dragged !== index
              ? dragged! < index
                ? 'after'
                : 'before'
              : undefined
          }
          draggable
          onDragStart={(event: DragEvent) => {
            event.dataTransfer.effectAllowed = 'move';
            setDragged(index);
          }}
          onDragOver={(event: DragEvent) => {
            event.preventDefault();
            setOver(index);
          }}
          onDragEnd={() => {
            setDragged(undefined);
            setOver(undefined);
          }}
          onDrop={(event: DragEvent) => {
            event.preventDefault();

            if (dragged !== undefined) {
              move(dragged, index);
            }

            setDragged(undefined);
            setOver(undefined);
          }}
        >
          <GripVertical size={16} className={styles['order__grip']} aria-hidden="true" />
          <span className={styles['order__index']}>{index + 1}</span>
          <span className={styles['order__title']}>
            {record.title === '' ? 'Без названия' : record.title}
          </span>
          <span className={styles['order__arrows']}>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Выше"
              disabled={index === 0}
              onClick={() => {
                move(index, index - 1);
              }}
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Ниже"
              disabled={index === records.length - 1}
              onClick={() => {
                move(index, index + 1);
              }}
            >
              <ArrowDown size={14} />
            </Button>
          </span>
        </li>
      ))}
    </ol>
  );
}
